// Authentication state for the UI.
//
// Note what this store does NOT hold: any token. Inside Revit the access token lives in the C# host
// and is attached to API calls there; the UI only ever learns whether someone is signed in and who.
//
// The browser has no host and holds a session cookie instead, set by the Identity pages the API
// itself serves. Both environments answer the same GetAuthState with the same shape, so everything
// downstream reads one thing — the only difference is how signing in starts: the plugin opens the
// system browser on an OAuth flow, the storefront simply navigates to the login page.
//
// A hand-rolled reactive singleton rather than a store library: the state is three fields and two
// actions, and every view needs the same instance. A store library would be ceremony here.

import { transport, CommandUnavailableError } from "@/transport";

export interface AuthState {
  status: "unknown" | "signed-out" | "signed-in" | "signing-in";
  email: string | null;
  displayName: string | null;
  /** Roles the account holds; drives role-gated UI like the moderation queue. */
  roles: string[];
  /** True when the account may moderate (holds the Moderator role). */
  isModerator: boolean;
  /** True when the account owns the platform: may create organizations and appoint their admins. */
  isSuperAdmin: boolean;
  /** The organization the account belongs to, or null for an individual account. */
  organizationId: string | null;
  organizationName: string | null;
  organizationSlug: string | null;
  /** "member" | "admin" within that organization; null when in none. Scoped, never global. */
  orgRole: string | null;
  /** True when the account administers its own organization (manages its member list). */
  isOrgAdmin: boolean;
  /** True when this environment offers a way to sign in. Both do now. */
  canSignIn: boolean;
  error: string | null;
}

interface AuthStateDto {
  signedIn: boolean;
  email?: string | null;
  displayName?: string | null;
  roles?: string[] | null;
  isModerator?: boolean;
  isSuperAdmin?: boolean;
  isOrgAdmin?: boolean;
  organizationId?: string | null;
  organizationName?: string | null;
  organizationSlug?: string | null;
  orgRole?: string | null;
}

const state = $state<AuthState>({
  status: "unknown",
  email: null,
  displayName: null,
  roles: [],
  isModerator: false,
  isSuperAdmin: false,
  organizationId: null,
  organizationName: null,
  organizationSlug: null,
  orgRole: null,
  isOrgAdmin: false,
  canSignIn: true,
  error: null,
});

function apply(dto: AuthStateDto) {
  state.status = dto.signedIn ? "signed-in" : "signed-out";
  state.email = dto.email ?? null;
  state.displayName = dto.displayName ?? null;
  state.roles = dto.roles ?? [];
  state.isModerator = dto.isModerator ?? false;
  state.isSuperAdmin = dto.isSuperAdmin ?? false;
  state.organizationId = dto.organizationId ?? null;
  state.organizationName = dto.organizationName ?? null;
  state.organizationSlug = dto.organizationSlug ?? null;
  state.orgRole = dto.orgRole ?? null;
  state.isOrgAdmin = dto.isOrgAdmin ?? dto.orgRole === "admin";
  state.error = null;
}

/** Reads current state, restoring a persisted session inside Revit. Safe in the browser too. */
async function refresh(): Promise<void> {
  try {
    const dto = await transport.invoke<AuthStateDto>("GetAuthState");
    apply(dto);
  } catch (error) {
    if (error instanceof CommandUnavailableError) {
      // Neither environment can answer — nothing to report but signed-out, and no sign-in to offer.
      state.status = "signed-out";
      state.canSignIn = false;
    } else {
      state.status = "signed-out";
      state.error = error instanceof Error ? error.message : String(error);
    }
  }
}

/**
 * Starts an interactive sign-in.
 *
 * Inside Revit that is an OAuth flow in the system browser, run by the host — never an embedded
 * WebView, so the user sees the real URL and can use their password manager and 2FA, and the plugin
 * is never in a position to read their credentials.
 *
 * In the browser it is a plain navigation to the API's own login page, which returns here
 * afterwards. Same principle, less machinery: the credential is entered on the service's own origin
 * and this page never touches it.
 */
async function signIn(): Promise<void> {
  if (!state.canSignIn) return;

  if (!transport.caps.revit) {
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${apiBaseUrl()}/Account/Login?returnUrl=${returnUrl}`;
    return;
  }

  state.status = "signing-in";
  state.error = null;
  try {
    const dto = await transport.invoke<AuthStateDto>("SignIn");
    apply(dto);
  } catch (error) {
    state.status = "signed-out";
    state.error = error instanceof Error ? error.message : String(error);
  }
}

/** Where the API lives, for the one case that leaves the SPA entirely: the login page. */
function apiBaseUrl(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5080").replace(/\/$/, "");
}

async function signOut(): Promise<void> {
  try {
    const dto = await transport.invoke<AuthStateDto>("SignOut");
    apply(dto);
  } catch {
    // Even if the host call fails, drop the UI to signed-out — leaving it "signed-in" after a
    // sign-out attempt is the worse failure.
    state.status = "signed-out";
    state.email = null;
    state.displayName = null;
    state.roles = [];
    state.isModerator = false;
    state.isSuperAdmin = false;
    state.organizationId = null;
    state.organizationName = null;
    state.organizationSlug = null;
    state.orgRole = null;
    state.isOrgAdmin = false;
  }
}

export const auth = {
  // No readonly() equivalent exists for $state — the returned proxy is mutable. The guarantee that
  // state only changes through refresh/signIn/signOut is a convention here, not a type-checked one.
  get state() {
    return state;
  },
  refresh,
  signIn,
  signOut,
};
